import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { clearAccessToken, getAccessToken, signalLogout } from "./auth-seam";
import { AUTH_REFRESH_PATH } from "./config";
import { refreshAccessToken } from "./refresh";
import { toApiError } from "./errors";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * Marks a request whose 401 means "nobody is signed in", NOT "the session died" — i.e. the
     * `/auth/me` identity probe, whose callers (`tryGetProfile`, `useMe`) all treat failure as
     * "anonymous".
     *
     * Refresh is still attempted: a signed-in visitor on a fresh page load has no in-memory access
     * token and depends on the cookie being exchanged. What changes is the FAILURE path — it stays
     * local (drop the dead token) instead of calling `signalLogout()`, which the app shell turns
     * into an app-wide redirect to login.
     *
     * Without this, every public page that asks "is anyone signed in?" threw anonymous visitors at
     * the login screen: `/apply/$roundId/register` was unreachable for exactly the people it exists
     * for.
     */
    optionalAuth?: boolean;
  }
}

/** Per-request marker so a request is retried at most once after a refresh. */
interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

/**
 * Normalize every rejection into an {@link ApiError} for the PUBLIC instance (login, signup,
 * refresh — no bearer, no refresh-retry). `privateApi` gets this for free from the auth
 * interceptor, which already throws `toApiError` on every path; the public instance has no such
 * interceptor, so without this its callers would receive a raw AxiosError and `isApiError`
 * guards (e.g. a login mutation's onError) would silently skip. Install AFTER http-logging so
 * logging still observes the raw error first.
 */
export function installErrorNormalization(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => Promise.reject(toApiError(error))
  );
}

/**
 * Install the auth request/response interceptors on an authenticated instance.
 *
 * Request:  attach the in-memory access token as `Authorization: Bearer`.
 * Response: on 401, run the single-flight refresh, then retry the original request once.
 *
 * 401 alone is the right gate again: the backend now answers a dead session with
 * `401 WWW-Authenticate: Bearer` and reserves 403 for authenticated permission failures, so the
 * two cases no longer need telling apart by error code.
 *
 * The instance is passed in (rather than imported) so the retry can re-issue on the same
 * instance without creating an import cycle.
 */
export function installAuthInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as RetryableConfig | undefined;
      const isRefreshCall = original?.url?.includes(AUTH_REFRESH_PATH) ?? false;
      const apiError = toApiError(error);

      // Refresh only on a genuine access-token 401, at most once per request, and NEVER for the
      // refresh endpoint itself (infinite-loop guard).
      if (
        error.response?.status === 401 &&
        original &&
        !original._retried &&
        !isRefreshCall
      ) {
        original._retried = true;
        try {
          const token = await refreshAccessToken(); // shared single-flight promise
          original.headers.set("Authorization", `Bearer ${token}`);
          return await instance.request(original);
        } catch {
          if (original.optionalAuth) {
            // An identity PROBE that could not refresh. Nobody was signed in, so there is no
            // session to lose and nothing to redirect away from: drop the dead token and let the
            // caller read the ApiError as "anonymous". Broadcasting logout here is what used to
            // bounce visitors off public pages.
            clearAccessToken();
          } else {
            // Refresh failed on a real request → the session really is gone. Drop the token and
            // signal logout; the app owns the redirect.
            signalLogout();
          }
          // Surface the original error as a normalized ApiError either way.
          throw apiError;
        }
      }

      throw apiError;
    }
  );
}
